<script lang="ts">
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { UPGRADES, type UpgradeDef } from '../core/balance';
  import { famousFound, medals, purchaseStatus, twinPairs, type GameEvent } from '../core/engine';
  import { formatFactors, isPrime, isTwinPrime } from '../core/numbers';
  import { famousAt } from '../core/famous';
  import { sound } from '../shared/lib/audio';
  import { storageKey } from '../shared/lib/edition';
  import { startFeedback } from '../shared/lib/feedback';
  import { fmt } from '../shared/lib/format';
  import { game } from '../shared/lib/game.svelte';
  import { settings } from '../shared/lib/settings.svelte';
  import { storage } from '../shared/lib/storage';
  import Ledger from './Ledger.svelte';
  import Line from './Line.svelte';
  import { log } from './log.svelte';
  import { LOGGED_MEDALS, VOICE } from './voice';

  const s = $derived(game.s);
  let place = $state<'line' | 'ledger'>('line');
  let confirmRestart = $state(false);
  let stepBtn = $state<HTMLButtonElement>();

  // ----- lights: follow the device until the player chooses -----
  const LIGHTS_KEY = storageKey('lights');
  let lights = $state<'on' | 'off' | null>(
    (storage.get(LIGHTS_KEY) as 'on' | 'off' | null) ?? null,
  );
  const systemDark =
    typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = $derived(lights === 'off' || (lights === null && systemDark));
  $effect(() => {
    if (lights) document.documentElement.dataset.lights = lights;
    else delete document.documentElement.dataset.lights;
  });
  function toggleLights() {
    lights = dark ? 'on' : 'off';
    storage.set(LIGHTS_KEY, lights);
  }

  $effect(() => {
    sound.enabled = settings.sound;
    sound.setVolume(settings.volume);
    settings.persist();
  });

  // ----- what's visible; derived from monotonic totals, so nothing ever disappears -----
  const showStores = $derived(s.lifetimePoints >= 1);
  const showBuild = $derived(s.maxVisited >= 8 && s.lifetimePoints >= 30);
  const showLedger = $derived(medals(s) >= 10);
  // Offered once within reach; finished ones leave the list and show under "built".
  const builds = $derived(
    UPGRADES.filter((u) => {
      const st = purchaseStatus(s, u.id);
      if (st.kind === 'maxed') return false;
      if (s.upgrades[u.id] > 0) return true;
      return st.kind === 'available' && st.cost <= Math.max(2 * s.lifetimePoints, 30);
    }),
  );
  const built = $derived(UPGRADES.filter((u) => s.upgrades[u.id] > 0));

  const here = $derived.by(() => {
    const n = s.position;
    const f = famousAt(n);
    if (isPrime(n)) return isTwinPrime(n) ? 'prime. a twin.' : 'prime.';
    if (f.length) return `${f[0]!.name.toLowerCase()}.`;
    if (n === 0 || n === 1) return n === 0 ? 'the start.' : 'unity.';
    return formatFactors(n);
  });

  function step() {
    sound.unlock();
    game.tap();
    if (!settings.reducedMotion) {
      stepBtn?.querySelector('.bar')?.animate(
        [
          { transform: 'scaleX(0)', opacity: 1 },
          { transform: 'scaleX(1)', opacity: 0 },
        ],
        {
          duration: 260,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        },
      );
    }
  }

  function turn() {
    if (game.flip()) log.say(s.direction === 1 ? VOICE.forward : VOICE.back);
  }

  function build(u: UpgradeDef) {
    sound.unlock();
    const level = game.buy(u.id);
    if (level === null) return;
    sound.purchase();
    log.say(
      u.id === 'horizon'
        ? `${VOICE.bought.horizon} ${u.effect(level).toLowerCase()}.`
        : VOICE.bought[u.id],
    );
  }

  function restart() {
    game.reset();
    log.clear();
    log.say(VOICE.restart);
    log.say(VOICE.start);
    confirmRestart = false;
    place = 'line';
  }

  // ----- narration -----
  function onEvents(events: readonly GameEvent[]) {
    for (const e of events) {
      if (e.type === 'step' && e.fresh && e.n === 1 && s.maxVisited === 1) log.say(VOICE.firstStep);
      else if (e.type === 'prime') {
        if (e.index === 1) log.say(VOICE.firstPrime);
        else if (LOGGED_MEDALS.has(e.index)) log.say(VOICE.medal(fmt(e.n), fmt(e.index)));
      } else if (e.type === 'twin') log.say(VOICE.twins(fmt(e.lo), fmt(e.hi)), 'twin');
      else if (e.type === 'famous') {
        // Just the name; the full story waits in the ledger.
        for (const f of e.entries) log.say(`${fmt(f.n)}. ${f.name.toLowerCase()}.`);
      } else if (e.type === 'blocked') {
        log.say(e.edge === 'ceiling' ? VOICE.edge(fmt(e.limit)) : VOICE.floor, 'edge');
      }
    }
  }

  let announcedBuild = false;
  let announcedLedger = false;
  $effect(() => {
    if (showBuild && !announcedBuild) {
      announcedBuild = true;
      if (s.lifetimePoints < 40) log.say(VOICE.buildsAppear);
    }
    if (showLedger && !announcedLedger) {
      announcedLedger = true;
      if (medals(s) < 15) log.say(VOICE.ledgerAppears);
    }
  });

  function onKey(e: KeyboardEvent) {
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || place !== 'line') return;
    if ((e.target as Element).closest('input, textarea, button')) return;
    if (e.code === 'Space') {
      e.preventDefault();
      step();
    }
  }

  onMount(() => {
    if (log.lines.length === 0)
      log.say(game.s.taps === 0 ? VOICE.start : `${fmt(game.s.position)}. still here.`);
    const stopFeedback = startFeedback({ toasts: false });
    const off = game.on(onEvents);
    let raf = 0;
    let prev = performance.now();
    const loop = (now: number) => {
      game.frame(now - prev, now);
      prev = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const flush = () => game.save();
    const onVisibility = () => document.visibilityState === 'hidden' && flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      cancelAnimationFrame(raf);
      stopFeedback();
      off();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  });

  const d = $derived(settings.reducedMotion ? 0 : 1);
</script>

<svelte:window onkeydown={onKey} />

<div class="room">
  <nav class="places" aria-label="Places">
    <button class:here={place === 'line'} onclick={() => (place = 'line')}>the line</button>
    {#if showLedger}
      <span class="sep" aria-hidden="true" in:fade={{ duration: 1200 * d }}>|</span>
      <button
        class:here={place === 'ledger'}
        onclick={() => (place = 'ledger')}
        in:fade={{ duration: 1200 * d }}
      >
        the ledger
      </button>
    {/if}
  </nav>

  <div class="cols">
    <section class="main">
      {#if place === 'line'}
        <div class="where">
          <p class="n num">{fmt(s.position)}</p>
          <p class="what">{here}</p>
        </div>

        {#if s.upgrades.radar > 0}
          <div in:fade={{ duration: 1200 * d }}><Line /></div>
        {/if}

        <div class="actions">
          <button class="act" bind:this={stepBtn} onclick={step}>
            {s.direction === 1 ? 'step forward' : 'step back'}<span class="bar" aria-hidden="true"
            ></span>
          </button>
          {#if s.upgrades.inverter > 0}
            <button class="act" onclick={turn} in:fade={{ duration: 900 * d }}>turn around</button>
          {/if}
        </div>

        {#if showBuild && builds.length > 0}
          <div class="build" in:fade={{ duration: 1200 * d }}>
            <h2>build:</h2>
            <div class="actions">
              {#each builds as u (u.id)}
                {@const st = purchaseStatus(s, u.id)}
                {@const lv = s.upgrades[u.id]}
                <button
                  class="act"
                  disabled={st.kind !== 'available' || !st.affordable}
                  onclick={() => build(u)}
                  title={st.kind === 'maxed' ? u.effect(lv) : u.effect(lv + 1)}
                  in:fade={{ duration: 900 * d }}
                >
                  {u.name.toLowerCase()}{#if u.costs.length > 1 && lv > 0}&nbsp;{lv +
                      (st.kind === 'maxed' ? 0 : 1)}{/if}
                  <span class="cost num">
                    {#if st.kind === 'maxed'}done{:else if st.kind === 'locked'}needs {st.needs.toLowerCase()}{:else}{fmt(
                        st.cost,
                      )}{/if}
                  </span>
                </button>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <Ledger />
      {/if}
    </section>

    {#if showStores}
      <aside class="stores" in:fade={{ duration: 1200 * d }}>
        <fieldset>
          <legend>stores</legend>
          <dl>
            <div>
              <dt>points</dt>
              <dd class="num">{fmt(s.points)}</dd>
            </div>
            {#if s.maxVisited >= 2}<div>
                <dt>primes</dt>
                <dd class="num">{fmt(medals(s))}</dd>
              </div>{/if}
            <div>
              <dt>furthest</dt>
              <dd class="num">{fmt(s.maxVisited)}</dd>
            </div>
            {#if s.minVisited < 0}<div>
                <dt>deepest</dt>
                <dd class="num">{fmt(s.minVisited)}</dd>
              </div>{/if}
            {#if famousFound(s).length > 1}<div>
                <dt>of note</dt>
                <dd class="num">{famousFound(s).length}</dd>
              </div>{/if}
            {#if s.upgrades.resonator > 0}<div>
                <dt>twins</dt>
                <dd class="num">{fmt(twinPairs(s))}</dd>
              </div>{/if}
          </dl>
        </fieldset>
        {#if built.length > 0}
          <fieldset in:fade={{ duration: 1200 * d }}>
            <legend>built</legend>
            <dl>
              {#each built as u (u.id)}
                <div>
                  <dt>{u.name.toLowerCase()}</dt>
                  <dd class="num">{u.costs.length > 1 ? s.upgrades[u.id] : ''}</dd>
                </div>
              {/each}
            </dl>
          </fieldset>
        {/if}
      </aside>
    {/if}

    <section class="log" aria-live="polite" aria-label="What has happened">
      {#each log.lines as line, i (line.id)}
        <p style:opacity={Math.max(0.12, 1 - i * 0.11)} in:fade={{ duration: 700 * d }}>
          {line.text}
        </p>
      {/each}
    </section>
  </div>

  <footer>
    <button onclick={toggleLights}>{dark ? 'lights on.' : 'lights off.'}</button>
    <button onclick={() => (settings.sound = !settings.sound)}
      >{settings.sound ? 'sound off.' : 'sound on.'}</button
    >
    {#if confirmRestart}
      <span
        >restart? <button onclick={restart}>yes.</button>
        <button onclick={() => (confirmRestart = false)}>no.</button></span
      >
    {:else}
      <button onclick={() => (confirmRestart = true)}>restart.</button>
    {/if}
    <a href="../index.html">all versions.</a>
  </footer>
</div>
